/**
 * Video QC — checks campaign videos against the brief.
 * Base URL: API_HOST (the main API, same as the campaign list and feeds)
 * Base path: /api/v1/campaign/:campaignId/qc
 *
 * The QC engine is a separate service that only accepts a server-side API
 * key, so the browser never calls it directly. These campaign-scoped routes
 * on the main backend forward to it; response bodies are the QC
 * service's objects, unchanged. Contract for the backend:
 * src/components/PlannerV2/components/VideoQC/BACKEND_CONTRACT.md
 *
 * Until those routes ship, calls fail with QcBackendUnavailable. Where
 * FEATURE_VIDEO_QC_SAMPLE is on (local development), the tab then shows
 * clearly-flagged sample data (see VideoQC/sampleQc.js); elsewhere it says
 * the feature isn't connected. A network failure is NOT treated as "not
 * deployed": it surfaces as an error with a retry, never as fake data.
 */

import { API_HOST } from '../config';

export class QcBackendUnavailable extends Error {
  constructor(message = 'Video QC backend is not available') {
    super(message);
    this.name = 'QcBackendUnavailable';
  }
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access')}`,
});

const parseResponseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
};

// FastAPI answers an unknown route with 404 {"detail": "Not Found"}; a proxy
// or CDN answers with an HTML page. Either way the QC routes aren't deployed.
// The QC routes themselves answer a real not-found with the
// { success: false, error } envelope (see BACKEND_CONTRACT.md), never this.
const isMissingRoute = (status, body) =>
  status === 404 && (!body || (body.detail === 'Not Found' && !body.error) || /<html/i.test(body.message || ''));

// The routes exist but the main API has no Video QC service to forward to
// (VIDEO_QC_BASE_URL unset). It answers 503 qc_not_configured in the error
// envelope rather than the bare 404 above, deliberately, so a *deployed* proxy
// is never mistaken for a missing one. For the page it is the same situation
// as a missing route, though: nothing to show but "not connected yet", or the
// labelled sample data where that is allowed. Without this it surfaced as a
// "Couldn't load Video QC" error with a Retry button that can never work.
const isNotConfigured = (status, body) =>
  status === 503 && body && typeof body === 'object' && body.error && body.error.code === 'qc_not_configured';

const handleResponse = async (response) => {
  const body = await parseResponseBody(response);
  if (response.ok) return body && body.success === true && 'data' in body ? body.data : body;
  if (isNotConfigured(response.status, body)) throw new QcBackendUnavailable('Video QC is not configured on this server');
  if (isMissingRoute(response.status, body)) throw new QcBackendUnavailable();
  return Promise.reject(body && typeof body === 'object' ? body : { message: `Request failed with status ${response.status}` });
};

const request = (path, { method = 'GET', body } = {}) =>
  fetch(path, { method, headers: getHeaders(), body: body === undefined ? undefined : JSON.stringify(body) })
    .catch(() => Promise.reject({ message: 'Could not reach the server. Check your connection and retry.' }))
    .then(handleResponse);

// REACT_APP_VIDEO_QC_API_HOST points only these routes elsewhere, e.g. a
// local QC stack during development; it defaults to the main API.
const QC_HOST = process.env.REACT_APP_VIDEO_QC_API_HOST || API_HOST;
const base = (campaignId) => `${QC_HOST}/api/v1/campaign/${campaignId}/qc`;

/** GET /qc → { brief, checklist, videos: [video summary] } */
export const getQcOverview = (campaignId) => request(base(campaignId));

/** PUT /qc/brief → { brief, checklist } */
export const saveQcBrief = (campaignId, { brief, checklist }) =>
  request(`${base(campaignId)}/brief`, { method: 'PUT', body: { brief, checklist } });

/** POST /qc/checklist/extract → { brief, checklist } (AI draft from the brief) */
export const extractQcChecklist = (campaignId) =>
  request(`${base(campaignId)}/checklist/extract`, { method: 'POST' });

/** POST /qc/videos — submit a video (queued for QC straight away) */
export const createQcVideo = (campaignId, { source, title, external_ref = null }) =>
  request(`${base(campaignId)}/videos`, { method: 'POST', body: { source, title, external_ref, run_qc: true } });

/** GET /qc/videos/:id → video with latest_check, frames (signed URLs), transcript */
export const getQcVideo = (campaignId, qcVideoId) => request(`${base(campaignId)}/videos/${qcVideoId}`);

/** GET /qc/videos/:id/checks → every AI run and human decision, newest first */
export const getQcHistory = (campaignId, qcVideoId) => request(`${base(campaignId)}/videos/${qcVideoId}/checks`);

/** POST /qc/videos/:id/runs — re-run QC */
export const runQc = (campaignId, qcVideoId) =>
  request(`${base(campaignId)}/videos/${qcVideoId}/runs`, { method: 'POST' });

/**
 * POST /qc/videos/:id/decisions — approve / override. The backend sets
 * decided_by from the login. check_id is the result the reviewer was looking
 * at; a 409 means a newer run finished in the meantime.
 */
export const decideQc = (campaignId, qcVideoId, { verdict, note, check_id }) =>
  request(`${base(campaignId)}/videos/${qcVideoId}/decisions`, { method: 'POST', body: { verdict, note, check_id } });

/** POST /qc/uploads → { upload_url, method, headers, source, max_bytes } */
export const createQcUpload = (campaignId, { filename, content_type }) =>
  request(`${base(campaignId)}/uploads`, { method: 'POST', body: { filename, content_type } });

/** Send the file straight to storage with the signed URL (no auth header). */
export async function uploadQcFile(upload, file, { signal } = {}) {
  let res;
  try {
    res = await fetch(upload.upload_url, { method: upload.method || 'PUT', headers: upload.headers, body: file, signal });
  } catch (err) {
    // A cancel is the user's own doing and is handled by the caller as such.
    if (err && err.name === 'AbortError') throw err;
    // The browser reports a blocked cross-origin PUT (storage without a CORS
    // rule for this app), an offline network and a dropped connection all as
    // the same bare "Failed to fetch", which tells nobody anything.
    throw new Error('Could not send the file to storage. Check your connection and try again; if it keeps happening, tell the team.');
  }
  if (!res.ok) throw new Error(`Upload failed with status ${res.status}`);
  return upload.source;
}
